import { formatPrice, type AuctionCurrency } from "./currency.js";
import { demoAuctionRules, DemoError } from "./demo.js";
import { Refusal } from "./franchises.js";
import { supabase } from "./supabase.js";
import type {
  AuctionForcedPrice,
  AuctionRules,
  AuctionRulesSetup,
  AuctionSlab,
  SquadFill,
} from "./types.js";

interface SlabRow {
  from_price: number;
  step: number;
}

interface RulesRow {
  event_id: string | null;
  purse_reserve: boolean;
  leftovers: AuctionRules["leftovers"];
  unsold: AuctionRules["unsold"];
  next_up: AuctionRules["nextUp"];
  forced_price: AuctionForcedPrice;
  board_rating: boolean;
  board_level: boolean;
  board_hand_side: boolean;
  auction_rule_slabs: SlabRow[];
}

const RULES_COLUMNS =
  "event_id, purse_reserve, leftovers, unsold, next_up, forced_price, board_rating, board_level, board_hand_side, auction_rule_slabs (from_price, step)";

function refuse(error: { message: string; code?: string | null }): never {
  throw new Refusal(error.message, error.code ?? null);
}

export function toAuctionRules(row: RulesRow): AuctionRules {
  return {
    purseReserve: row.purse_reserve,
    leftovers: row.leftovers,
    unsold: row.unsold,
    nextUp: row.next_up,
    forcedPrice: row.forced_price,
    board: {
      rating: row.board_rating,
      level: row.board_level,
      handSide: row.board_hand_side,
    },
    slabs: row.auction_rule_slabs
      .map((s) => ({ from: s.from_price, step: s.step }))
      .sort((a, b) => a.from - b.from),
  };
}

/**
 * The tournament's rules and each pool's own. Null for a tournament with no rules, which is
 * one that is not an auction, or one the caller cannot see.
 */
export async function fetchAuctionRules(tournamentId: string): Promise<AuctionRulesSetup | null> {
  if (!supabase) return demoAuctionRules(tournamentId);

  const { data, error } = await supabase
    .from("auction_rules")
    .select(RULES_COLUMNS)
    .eq("tournament_id", tournamentId);
  if (error) refuse(error);

  const rows = (data ?? []) as unknown as RulesRow[];
  const base = rows.find((row) => row.event_id === null);
  if (!base) return null;

  return {
    tournament: toAuctionRules(base),
    pools: new Map(
      rows
        .filter((row) => row.event_id !== null)
        .map((row) => [row.event_id!, toAuctionRules(row)]),
    ),
  };
}

/** Replace one set whole: the tournament's with a null `eventId`, else that pool's own. */
export async function saveAuctionRules(
  tournamentId: string,
  eventId: string | null,
  rules: AuctionRules,
): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("save_auction_rules", {
    target_tournament: tournamentId,
    target_event: eventId,
    new_purse_reserve: rules.purseReserve,
    new_leftovers: rules.leftovers,
    new_unsold: rules.unsold,
    new_next_up: rules.nextUp,
    from_prices: rules.slabs.map((s) => s.from),
    steps: rules.slabs.map((s) => s.step),
    new_board_rating: rules.board.rating,
    new_board_level: rules.board.level,
    new_board_hand_side: rules.board.handSide,
    new_forced_price: rules.forcedPrice,
  });
  if (error) refuse(error);
}

/** Give a pool rules of its own, a copy of the tournament's as they stand. */
export async function givePoolOwnRules(eventId: string): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("give_pool_own_rules", { target_event: eventId });
  if (error) refuse(error);
}

/** How a pool is filled: by auction, or placed by the organiser. Until the auction starts. */
export async function setSquadFill(eventId: string, fill: SquadFill): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("set_squad_fill", { target_event: eventId, fill });
  if (error) refuse(error);
}

/** Put a pool back on the tournament's rules. */
export async function dropPoolOwnRules(eventId: string): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("drop_pool_own_rules", { target_event: eventId });
  if (error) refuse(error);
}

/**
 * What is wrong with each slab, by index, in the database's words; an empty map when nothing is.
 * A slab is judged against the one before it, so the first mistake in a run is the one named.
 */
export function slabErrors(slabs: readonly AuctionSlab[]): Map<number, string> {
  const errors = new Map<number, string>();
  slabs.forEach((slab, i) => {
    if (!Number.isInteger(slab.from) || !Number.isInteger(slab.step)) {
      errors.set(i, "Use whole points");
    } else if (i === 0 && slab.from !== 0) {
      errors.set(i, "The first slab must start at 0");
    } else if (i > 0 && slab.from <= slabs[i - 1]!.from) {
      errors.set(i, "Each slab must start above the one before");
    } else if (slab.step < 1) {
      errors.set(i, "A step must be at least 1");
    }
  });
  return errors;
}

/** The rules in force for a pool: its own when it has them, the tournament's otherwise. */
export function rulesFor(eventId: string, setup: AuctionRulesSetup): AuctionRules {
  return setup.pools.get(eventId) ?? setup.tournament;
}

/** How far a bid raises `price`: the step of the last slab starting at or below it. */
export function stepAt(slabs: readonly AuctionSlab[], price: number): number {
  let step = slabs[0]?.step ?? 1;
  for (const slab of slabs) {
    if (slab.from <= price) step = slab.step;
  }
  return step;
}

export const LEFTOVERS_LABELS: Record<AuctionRules["leftovers"], string> = {
  capped_bidding: "Bid for, at most one extra per team",
  organiser: "The organiser assigns them",
  next_lot: "Carried into the next lot",
};

export const UNSOLD_LABELS: Record<AuctionRules["unsold"], string> = {
  organiser: "The organiser assigns them",
  unassigned: "Left without a squad",
};

export const NEXT_UP_LABELS: Record<AuctionRules["nextUp"], string> = {
  random: "Drawn at random from the lot",
  organiser: "The organiser chooses",
  seed: "In seed order",
};

export const FORCED_PRICE_LABELS: Record<AuctionForcedPrice, string> = {
  base: "The lot's base price",
  average: "The average of the pool's sales so far",
  lowest_and_base: "The average of the pool's lowest sale and the base price",
  lowest: "The pool's lowest sale so far",
};

/** "+5 from 0, +10 from 50": the slabs on one line. */
export function slabsLine(slabs: readonly AuctionSlab[], currency?: AuctionCurrency): string {
  const price = (n: number) => (currency ? formatPrice(n, currency) : String(n));
  return slabs.map((s) => `+${price(s.step)} from ${price(s.from)}`).join(", ");
}

export const SQUAD_FILL_LABELS: Record<SquadFill, string> = {
  auction: "Auction",
  assigned: "Organiser assigns",
};
